import { matcher, Entities, EntityKind } from "../entityKinds";
import GameState, { getXYindex } from "../gameState";
import { GameTurnLogic } from "../GameTurnLogic";

export enum RemovedBecause {
  ImpactSolid = "ImpactSolid",
  Broken = "Broken",
  BlownUp = "BlownUp",
  Shot = "Shot",
  Fallen = "Fallen",
}

// Read this as how do things handle overlapping something else.
const overlapStaticWall = matcher<boolean>({
  [EntityKind.StaticWall]: () => false,
  [EntityKind.Crate]: () => false,
  [EntityKind.ExtendedBlock]: () => false,
  [EntityKind.Empty]: () => false,
  [EntityKind.PlayerBullet]: () => false,
  [EntityKind.MeleeEnemy]: () => false,
  [EntityKind.PressurePlate]: () => false,
  [EntityKind.Door]: () => false,
  [EntityKind.PlayerGun]: () => false,
  [EntityKind.PlayerBody]: () => false,
  [EntityKind.TDamage]: () => false,
  [EntityKind.WinLevel]: () => false,
  [EntityKind.LazorToggle]: () => false,
});

const overlapCrate = (gameState: GameState) =>
  matcher<boolean>({
    [EntityKind.StaticWall]: () => false,
    [EntityKind.Crate]: () => false,
    [EntityKind.ExtendedBlock]: () => false,
    [EntityKind.Empty]: () => false,
    [EntityKind.PlayerBullet]: (e) => {
      return false;
    },
    [EntityKind.MeleeEnemy]: () => false,
    [EntityKind.PressurePlate]: () => false,
    [EntityKind.Door]: () => false,
    [EntityKind.PlayerGun]: () => false,
    [EntityKind.PlayerBody]: () => false,
    [EntityKind.TDamage]: () => false,
    [EntityKind.WinLevel]: () => false,
    [EntityKind.LazorToggle]: () => false,
  });

const overlapTDamage = (
  gameState: GameState,
  dirtyTiles: Set<number>,
  gameLogic: GameTurnLogic
) =>
  matcher<boolean>({
    [EntityKind.StaticWall]: () => false,
    [EntityKind.Crate]: (e) => {
      e.entity?.().removeFromBoard(RemovedBecause.ImpactSolid);
      if (e.explodes) {
        for (const x of [-1, 0, 1]) {
          for (const y of [-1, 0, 1]) {
            if (
              gameState.addEntities(
                {
                  kind: EntityKind.TDamage,
                  id: -1,
                  x: e.x + x,
                  y: e.y + y,
                  turns_left: 0,
                },
                e.x + x,
                e.y + y
              )
            ) {
              dirtyTiles.add(getXYindex(e.x + x, e.y + y));
            }
          }
        }
      }
      return true;
    },
    [EntityKind.ExtendedBlock]: (e) => {
      e.entity?.().removeFromBoard(RemovedBecause.ImpactSolid);
      gameLogic.extendedBodiesBroken.set(e.id, {
        x: e.x,
        y: e.y,
        sharedBlockId: e.sharedBlockId,
        id: e.id,
      });
      return true;
    },
    [EntityKind.PlayerBullet]: () => false,
    [EntityKind.Empty]: () => false,
    [EntityKind.MeleeEnemy]: (e) => {
      e.entity?.().removeFromBoard(RemovedBecause.BlownUp);
      return true;
    },
    [EntityKind.PressurePlate]: () => false,
    [EntityKind.Door]: () => false,
    [EntityKind.PlayerGun]: () => false,
    [EntityKind.PlayerBody]: () => {
      return false;
    },
    [EntityKind.TDamage]: () => false,
    [EntityKind.WinLevel]: () => false,
    [EntityKind.LazorToggle]: () => false,
  });
const overlapPlayerBullet = (gameState: GameState) =>
  matcher<boolean>({
    [EntityKind.StaticWall]: () => false,
    [EntityKind.Crate]: (e) => {
      if (e.breaks) {
        gameState.addEntities(
          {
            kind: EntityKind.TDamage,
            id: -1,
            x: e.x,
            y: e.y,
            turns_left: 0,
          },
          e.x,
          e.y
        );
        return true;
      }
      return false;
    },
    [EntityKind.ExtendedBlock]: (e) => {
      gameState.addEntities(
        {
          kind: EntityKind.TDamage,
          id: -1,
          x: e.x,
          y: e.y,
          turns_left: 0,
        },
        e.x,
        e.y
      );
      return true;
    },
    [EntityKind.Empty]: () => false,
    [EntityKind.PlayerBullet]: (e) => false,
    [EntityKind.MeleeEnemy]: (e) => {
      e.entity?.().removeFromBoard(RemovedBecause.Shot);
      return true;
    },
    [EntityKind.PressurePlate]: () => false,
    [EntityKind.Door]: () => false,
    [EntityKind.PlayerGun]: () => false,
    [EntityKind.PlayerBody]: () => false,
    [EntityKind.TDamage]: () => false,
    [EntityKind.WinLevel]: () => false,
    [EntityKind.LazorToggle]: () => false,
  });

const overlapPlayerGun = matcher<boolean>({
  [EntityKind.StaticWall]: () => false,
  [EntityKind.Crate]: () => false,
  [EntityKind.ExtendedBlock]: () => false,
  [EntityKind.Empty]: () => false,
  [EntityKind.PlayerBullet]: () => false,
  [EntityKind.PressurePlate]: () => false,
  [EntityKind.MeleeEnemy]: () => false,
  [EntityKind.Door]: () => false,
  [EntityKind.PlayerGun]: () => false,
  [EntityKind.PlayerBody]: () => false,
  [EntityKind.TDamage]: () => false,
  [EntityKind.WinLevel]: () => false,
  [EntityKind.LazorToggle]: () => false,
});

const overlapEmpty = matcher<boolean>({
  [EntityKind.StaticWall]: () => false,
  [EntityKind.Crate]: (e) => {
    e.entity?.().removeFromBoard(RemovedBecause.Fallen);
    return true;
  },
  [EntityKind.ExtendedBlock]: () => false,
  [EntityKind.Empty]: () => false, // Handled at the end of the turn if the whole block is off the map.
  [EntityKind.PlayerBullet]: () => false,
  [EntityKind.PressurePlate]: () => false,
  [EntityKind.MeleeEnemy]: (e) => {
    e.entity?.().removeFromBoard(RemovedBecause.Fallen);
    return true;
  },
  [EntityKind.Door]: () => false,
  [EntityKind.PlayerGun]: () => false,
  [EntityKind.PlayerBody]: () => false,
  [EntityKind.TDamage]: () => false,
  [EntityKind.WinLevel]: () => false,
  [EntityKind.LazorToggle]: () => false,
});

const compoundOverlapMatcher = (
  firstEntity: Entities,
  secondEntity: Entities,
  gameState: GameState,
  dirtyTiles: Set<number>,
  gameLogic: GameTurnLogic
) =>
  matcher<boolean>({
    [EntityKind.StaticWall]: () => overlapStaticWall(secondEntity),
    [EntityKind.Crate]: () => overlapCrate(gameState)(secondEntity),
    [EntityKind.ExtendedBlock]: () => false,
    [EntityKind.Empty]: () => overlapEmpty(secondEntity),
    [EntityKind.PlayerBullet]: () =>
      overlapPlayerBullet(gameState)(secondEntity),
    [EntityKind.PressurePlate]: () => false,
    [EntityKind.MeleeEnemy]: () => false,
    [EntityKind.Door]: () => false,
    [EntityKind.PlayerGun]: () => overlapPlayerGun(secondEntity),
    [EntityKind.PlayerBody]: () => false,
    [EntityKind.TDamage]: () =>
      overlapTDamage(gameState, dirtyTiles, gameLogic)(secondEntity),
    [EntityKind.WinLevel]: () => false,
    [EntityKind.LazorToggle]: () => false,
  })(firstEntity);

export namespace Rules {
  export const handleOverlap = (
    entities: Entities[],
    gameState: GameState,
    dirtyTiles: Set<number>,
    gameLogic: GameTurnLogic
  ) => {
    for (let i = 0; i < entities.length - 1; i++) {
      for (let j = i + 1; j < entities.length; j++) {
        if (
          compoundOverlapMatcher(
            entities[i],
            entities[j],
            gameState,
            dirtyTiles,
            gameLogic
          ) ||
          compoundOverlapMatcher(
            entities[j],
            entities[i],
            gameState,
            dirtyTiles,
            gameLogic
          )
        ) {
          // by default add the tile itself if something has changed.
          // If we spawn cells elsewhere need to handle dirtyTiles manually.
          dirtyTiles.add(getXYindex(entities[i].x, entities[i].y));
        }
      }
    }
  };
}
